import { useCallback, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

/* ------------------------------------------------------------------ */
/*  useDermatologyAnalysis (PRD.217)                                  */
/*                                                                    */
/*  Calls the `pdermatologyAnalyse` Cloud Function with the selected  */
/*  encounter image ids. The function performs the Siksha call and    */
/*  persists the analysis run + AI Suggested diagnosis row server-    */
/*  side, then returns a slim summary.                                */
/*                                                                    */
/*  This hook owns transient request state only — the AI Suggested    */
/*  card + analysis result drawer read their data from the PowerSync  */
/*  mirror once the server-side rows land.                            */
/* ------------------------------------------------------------------ */

const DERM_AI_URL = import.meta.env.VITE_DERMATOLOGY_AI_URL;

export type DermAnalysisStatus = 'idle' | 'submitting' | 'success' | 'error';

export interface DermAnalysisRequest {
  orgId: string;
  patientId: string;
  encounterId: string;
  /** patient_files.id values — must be 1–5, all belonging to the encounter. */
  encounterImageIds: string[];
}

export interface DermAnalysisSummary {
  analysisRunId: string;
  status: 'completed' | 'failed';
  primaryDiagnosis?: string;
  confidence?: number;
  severity?: string;
  suggestionId?: string;
  errorMessage?: string;
}

export interface UseDermatologyAnalysisReturn {
  status: DermAnalysisStatus;
  error: string | null;
  lastResult: DermAnalysisSummary | null;
  /** True when the function is reachable (VITE_DERMATOLOGY_AI_URL configured). */
  enabled: boolean;
  /** Submit an analysis request. Returns the result (or null on error). */
  analyse: (input: DermAnalysisRequest) => Promise<DermAnalysisSummary | null>;
  /** Reset to idle (clears error + lastResult). */
  reset: () => void;
}

export function useDermatologyAnalysis(): UseDermatologyAnalysisReturn {
  const [status, setStatus] = useState<DermAnalysisStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<DermAnalysisSummary | null>(null);

  const enabled = Boolean(DERM_AI_URL);

  const analyse = useCallback(
    async (input: DermAnalysisRequest): Promise<DermAnalysisSummary | null> => {
      // Client-side guardrails mirroring the server handler so the UI fails
      // fast with a clear message before a network round trip.
      if (!DERM_AI_URL) {
        const msg = 'Dermatology AI endpoint not configured.';
        setError(msg);
        setStatus('error');
        return null;
      }
      if (!input.orgId || !input.patientId || !input.encounterId) {
        const msg = 'Missing encounter context.';
        setError(msg);
        setStatus('error');
        return null;
      }
      if (input.encounterImageIds.length === 0) {
        const msg = 'Select at least one image.';
        setError(msg);
        setStatus('error');
        return null;
      }
      if (input.encounterImageIds.length > 5) {
        const msg = 'You can analyze up to 5 images at a time. Please select images of the same lesion.';
        setError(msg);
        setStatus('error');
        return null;
      }

      setStatus('submitting');
      setError(null);

      try {
        const token = await getIdToken();
        const res = await fetch(DERM_AI_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            orgId: input.orgId,
            patientId: input.patientId,
            encounterId: input.encounterId,
            encounterImageIds: input.encounterImageIds,
          }),
        });

        // 200: completed run summary
        // 400/401/403: validation / auth / ownership errors → JSON { error }
        // 502: provider/download error → may carry a failed-run summary
        const body: unknown = await res.json().catch(() => null);

        if (!res.ok) {
          const message =
            (typeof body === 'object' && body !== null && 'error' in body
              ? String((body as { error?: unknown }).error ?? '')
              : '') ||
            (typeof body === 'object' && body !== null && 'errorMessage' in body
              ? String((body as { errorMessage?: unknown }).errorMessage ?? '')
              : '') ||
            `AI analysis failed (HTTP ${res.status}).`;
          setError(message);
          setStatus('error');
          // 502 includes a structured failed-run summary; surface it so the UI
          // can deep-link to the failed run if needed.
          if (
            res.status === 502 &&
            typeof body === 'object' &&
            body !== null &&
            'analysisRunId' in body
          ) {
            const failed = body as DermAnalysisSummary;
            setLastResult(failed);
            return failed;
          }
          return null;
        }

        const summary = body as DermAnalysisSummary;
        setLastResult(summary);
        setStatus('success');
        return summary;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'AI analysis failed.';
        setError(msg);
        setStatus('error');
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setLastResult(null);
  }, []);

  return { status, error, lastResult, enabled, analyse, reset };
}
